/**
 * Activity beacon for signed-in readers: reports a sign-in, a sign-up, or a
 * content page read to the ingest endpoint (POST {VITE_PDF_API_URL}/events,
 * behind the same Cognito JWT authorizer as the PDF download route).
 *
 * Dark-ship + privacy guarantees:
 *  - Does nothing unless auth is enabled at build time AND the API base is set
 *    (`activityEnabled`), and nothing for a signed-out reader (no ID token, so
 *    no report). Anonymous reading stays anonymous; only the separate,
 *    salted-hash meter ever sees it.
 *  - Fire-and-forget and best-effort: never throws, never blocks the UI, never
 *    surfaces an error. A missing endpoint or a network failure is a no-op.
 *  - The body carries only the event type and, for a page read, the path. The
 *    server derives the account (user_sub) from the verified token, never the
 *    body, so activity cannot be attributed to another account.
 */
import { authEnabled, getIdToken } from './auth';

const apiUrl = (import.meta.env.VITE_PDF_API_URL as string | undefined)
  ?.trim()
  .replace(/\/+$/, '');

/** True only when auth is on and the shared ingest API base is configured. */
export const activityEnabled = authEnabled && Boolean(apiUrl);

export type ActivityEvent = 'sign_in' | 'sign_up' | 'page_read';

export async function reportActivity(
  event: ActivityEvent,
  path?: string,
): Promise<void> {
  if (!activityEnabled) return;
  try {
    const token = await getIdToken();
    if (!token) return;
    const body =
      event === 'page_read' && path
        ? { event_type: event, path }
        : { event_type: event };
    await fetch(`${apiUrl}/events`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
  } catch {
    // Best-effort: a failed beacon must never disturb the reader.
  }
}
